import React, { useState, useEffect } from 'react';
import { MotionConfig } from 'framer-motion';
import './App.css';
import useLenis from './hooks/useLenis';
import { scrollToTop } from './lib/scroll';
import Header from './components/Header';
import Hero from './components/Hero';
import Company from './components/Company';
import Mission from './components/Mission';
import Products from './components/Products';
import Principles from './components/Principles';
import Team from './components/Team';
import Contact from './components/Contact';
import Footer from './components/Footer';
import ScrollProgress from './components/ScrollProgress';
import CookieConsent from './components/CookieConsent';
import LegalModal from './components/LegalModal';
import LegalPage from './components/LegalPage';
import BorderouPage from './components/BorderouPage';
import CorespondentaPage from './components/CorespondentaPage';
import OrtexoPage from './components/OrtexoPage';
import NotFoundPage from './components/NotFoundPage';

const pageMeta = {
  home: {
    title: 'Poly Tool Design — Software pentru profesioniștii din drept',
    description: 'Companie românească de software pentru avocați, practicieni în insolvență și cabinete de contabilitate. Trei aplicații: Ortexo (cercetare juridică cu sursa citată), Borderou (dosare, registru, timp și facturi) și Plicuri (corespondență de insolvență). Din 2018, din Constanța.',
  },
  ortexo: {
    title: 'Ortexo — Răspunsuri juridice cu sursa citată | Poly Tool',
    description: 'Întrebi în limbaj obișnuit și primești răspunsul cu articolul citat. Legislație, Cod fiscal, TVA, norme contabile și jurisprudență (inclusiv ÎCCJ), mereu la zi. Se conectează la Claude și ChatGPT.',
  },
  borderou: {
    title: 'Borderou — Dosare, registru și calendar | Poly Tool',
    description: 'Monitorizare dosare din portal.just.ro cu termene extrase de AI, dosare noi preluate automat, registru de ieșiri și calendar. Sincronizare în ambele sensuri cu Plicuri, fără introducere manuală.',
  },
  corespondenta: {
    title: 'Plicuri — Corespondență de insolvență, automat | Poly Tool',
    description: 'De la sentință la plic, automat. Un model AI (procesat în UE) citește sentința; plicuri, notificări, confirmări de primire și borderou dintr-un singur upload. Adrese din ANAF și VIES, credite fără abonament.',
  },
  'privacy-policy': {
    title: 'Politică de Confidenţialitate | Poly Tool',
    description: 'Politica de confidenţialitate a Poly Tool Design S.R.L. — cum colectăm, procesăm și protejăm datele tale personale.',
  },
  terms: {
    title: 'Termeni și Condiţii | Poly Tool',
    description: 'Termenii și condiţiile de utilizare a serviciilor Poly Tool Design S.R.L.',
  },
  cookies: {
    title: 'Politică Cookies | Poly Tool',
    description: 'Informaţii despre cookie-urile utilizate de Poly Tool Design S.R.L.',
  },
  dpa: {
    title: 'Acord de Prelucrare a Datelor (DPA) | Poly Tool',
    description: 'Acordul de Prelucrare a Datelor (DPA) al aplicaţiei Plicuri, conform art. 28 GDPR — Poly Tool procesator, clientul operator.',
  },
};

function getPageFromPath(pathname) {
  if (pathname === '/borderou') return 'borderou';
  if (pathname === '/corespondenta') return 'corespondenta';
  if (pathname === '/ortexo') return 'ortexo';
  if (pathname === '/privacy-policy') return 'privacy-policy';
  if (pathname === '/terms') return 'terms';
  if (pathname === '/cookies') return 'cookies';
  if (pathname === '/dpa') return 'dpa';
  if (pathname === '/') return 'home';
  return '404';
}

function App() {
  const [legalModal, setLegalModal] = useState({ isOpen: false, type: null });
  const [currentPage, setCurrentPage] = useState(() => getPageFromPath(window.location.pathname));

  useLenis();

  useEffect(() => {
    const meta = pageMeta[currentPage] || pageMeta.home;
    document.title = meta.title;
    let descEl = document.querySelector('meta[name="description"]');
    if (!descEl) {
      descEl = document.createElement('meta');
      descEl.setAttribute('name', 'description');
      document.head.appendChild(descEl);
    }
    descEl.setAttribute('content', meta.description);

    // Open Graph
    const setOg = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setOg('og:title', meta.title);
    setOg('og:description', meta.description);
    setOg('og:type', 'website');
  }, [currentPage]);

  useEffect(() => {
    const handleOpenLegal = (e) => {
      setLegalModal({ isOpen: true, type: e.detail });
    };
    window.addEventListener('openLegal', handleOpenLegal);
    return () => window.removeEventListener('openLegal', handleOpenLegal);
  }, []);

  useEffect(() => {
    const handlePop = () => setCurrentPage(getPageFromPath(window.location.pathname));
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  const navigate = (page) => {
    const path = page === 'home' ? '/' : `/${page}`;
    window.history.pushState({}, '', path);
    setCurrentPage(page);
    scrollToTop();
  };

  const openLegal = (type) => {
    setLegalModal({ isOpen: true, type });
  };

  const closeLegal = () => {
    setLegalModal({ isOpen: false, type: null });
  };

  return (
    <MotionConfig reducedMotion="user">
    <div className="App">
      <ScrollProgress />
      <Header onNavigate={navigate} currentPage={currentPage} />
      <main>
        <div key={currentPage} className="page-transition">
          {currentPage === 'home' && (
            <>
              <Hero onNavigate={navigate} />
              <Company />
              <Mission />
              <Products onNavigate={navigate} />
              <Principles />
              <Team />
              <Contact />
            </>
          )}
          {currentPage === 'borderou' && <BorderouPage onNavigate={navigate} />}
          {currentPage === 'corespondenta' && <CorespondentaPage onNavigate={navigate} />}
          {currentPage === 'ortexo' && <OrtexoPage onNavigate={navigate} />}
          {currentPage === 'privacy-policy' && <LegalPage type="privacy" onNavigate={navigate} />}
          {currentPage === 'terms' && <LegalPage type="terms" onNavigate={navigate} />}
          {currentPage === 'cookies' && <LegalPage type="cookies" onNavigate={navigate} />}
          {currentPage === 'dpa' && <LegalPage type="dpa" onNavigate={navigate} />}
          {currentPage === '404' && <NotFoundPage onNavigate={navigate} />}
        </div>
      </main>
      <Footer onNavigate={navigate} />

      <CookieConsent />

      <LegalModal
        isOpen={legalModal.isOpen}
        type={legalModal.type}
        onClose={closeLegal}
      />
    </div>
    </MotionConfig>
  );
}

export default App;
